// KnowsYou — the memory lens: exactly what the agent is told about you.
//
// Renders the three prompt blocks the agent actually receives (M1 about-you,
// M2 recent milestones, M3 the pedagogical frame), de-jargoned. The reframe it
// performs (view-agent-inspection.md §3): every fact traces to an action the
// author took — it knows your name because you told it, it knows your
// fractions quiz because you made one. So each block that folds to nothing
// renders an HONEST call to action, never a blank and never a placeholder.
//
// D8 (verbatim honesty): the plain-language view is the default; the raw
// composed block the model reads is kept behind one more disclosure step (a
// native <details>) for the auditor who wants max honesty. Pure/props-driven —
// every field arrives resolved; a milestone is a door (onReveal).

export { KnowsYou };

import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import type { KnowsYou as KnowsYouData, MemoryMilestone } from "../types.js";
import "./Inspection.css";

type KnowsYouProps = {
    readonly data: KnowsYouData;
    // Open the resource a milestone names (the ChatGPT "link to what was
    // captured" move). Absent ⇒ milestones render as static text.
    readonly onRevealMilestone?: (milestone: MemoryMilestone) => void;
};

const KnowsYou = ({ data, onRevealMilestone }: KnowsYouProps): ReactNode => (
    <div className="bilda-hi-inspect">
        <Section icon="person" title={<Trans>About you</Trans>}>
            {data.name !== undefined ? (
                <Text as="p" size="sm">
                    <Trans>Your name is {data.name}.</Trans>
                </Text>
            ) : (
                <Text as="p" size="sm" tone="dim">
                    <Trans>
                        You haven't told me your name yet — say it in chat and
                        I'll remember.
                    </Trans>
                </Text>
            )}
            {data.exportPreference !== undefined && (
                <Text as="p" size="sm">
                    <Trans>
                        You usually export to {data.exportPreference}.
                    </Trans>
                </Text>
            )}
        </Section>

        <Section icon="clock" title={<Trans>Recently, in your sessions</Trans>}>
            {data.milestones.length === 0 ? (
                <Text as="p" size="sm" tone="dim">
                    <Trans>
                        Nothing yet — your recent work will show here.
                    </Trans>
                </Text>
            ) : (
                <ul className="bilda-hi-inspect__list">
                    {data.milestones.map((milestone) => (
                        <li
                            key={milestone.id}
                            className="bilda-hi-inspect__milestone"
                        >
                            {onRevealMilestone ? (
                                <button
                                    type="button"
                                    className="bilda-hi-inspect__door"
                                    onClick={() => onRevealMilestone(milestone)}
                                >
                                    {milestone.text}
                                </button>
                            ) : (
                                <Text as="span" size="sm">
                                    {milestone.text}
                                </Text>
                            )}
                            <Text as="span" size="xs" tone="faint">
                                {milestone.when}
                            </Text>
                        </li>
                    ))}
                </ul>
            )}
        </Section>

        <Section
            icon="objective"
            title={<Trans>The frame I'm working within</Trans>}
        >
            {data.frame.length === 0 ? (
                <Text as="p" size="sm" tone="dim">
                    <Trans>
                        No active learning context — set one to guide how
                        materials are written.
                    </Trans>
                </Text>
            ) : (
                <ul className="bilda-hi-inspect__list">
                    {data.frame.map((line) => (
                        <li key={line}>
                            <Text as="span" size="sm">
                                {line}
                            </Text>
                        </li>
                    ))}
                </ul>
            )}
        </Section>

        {data.verbatimBlock !== undefined && (
            <details className="bilda-hi-inspect__verbatim">
                <summary className="bilda-hi-inspect__verbatim-summary">
                    <Icon name="chevron-right" size="xs" />
                    <Trans>See exactly what the agent reads</Trans>
                </summary>
                <pre className="bilda-hi-inspect__verbatim-body">
                    {data.verbatimBlock}
                </pre>
            </details>
        )}
    </div>
);

// One titled block — an icon + heading over its body. Whitespace frames it.
const Section = ({
    icon,
    title,
    children,
}: {
    icon: "person" | "clock" | "objective";
    title: ReactNode;
    children: ReactNode;
}): ReactNode => (
    <section className="bilda-hi-inspect__section">
        <div className="bilda-hi-inspect__section-head">
            <span aria-hidden="true" className="bilda-hi-inspect__section-icon">
                <Icon name={icon} size="sm" />
            </span>
            <h2 className="bilda-hi-inspect__section-title">{title}</h2>
        </div>
        <div className="bilda-hi-inspect__section-body">{children}</div>
    </section>
);
